<script setup lang="ts">
import { onMounted, ref } from 'vue'

const props = defineProps<{
  message: string
  clickable?: boolean
  onClick?: () => void
  actionLabel?: string
  actionAriaLabel?: string
  onAction?: () => void
  durationMs?: number
}>()

const notificationRef = ref<HTMLElement | null>(null) // Referenz zur Benachrichtigung

onMounted(() => {
  if (notificationRef.value) {
    const notification = notificationRef.value

    // Setze die CSS-Variable
    const notifications = Array.from(
      document.querySelectorAll('.notification'),
    )
    const index = notifications.indexOf(notification) // Richtigen Index im DOM ermitteln
    notification.style.setProperty('--notification-index', index.toString())
    if (props.durationMs) {
      notification.style.setProperty('--notification-duration', `${props.durationMs}ms`)
    }

    // Animation-Ende: Benachrichtigung entfernen
    const removeNotification = () => {
      notification.removeEventListener('animationend', removeNotification)
      notification.remove()
    }

    notification.addEventListener('animationend', removeNotification)
  }
})

const handleClick = () => {
  if (props.clickable && props.onClick) {
    props.onClick() // Callback-Funktion ausführen
    // emit("click"); // Event für die Elternkomponente auslösen
  }
}

const handleActionClick = (event: MouseEvent) => {
  event.stopPropagation()
  props.onAction?.()
}
</script>

<template>
  <div
    ref="notificationRef"
    class="notification"
    :class="{ clickable }"
    @click="handleClick"
  >
    <p>{{ message }}</p>
    <button
      v-if="actionLabel"
      type="button"
      class="notification__action"
      :aria-label="actionAriaLabel || actionLabel"
      @click="handleActionClick"
    >
      {{ actionLabel }}
    </button>
  </div>
</template>

<style scoped>
.notification {
  max-width: 90vw;
  padding: 1rem 2rem;
  border: none;
  background: var(--text-primary);
  color: white;
  font-weight: 900;
  font-size: 1rem;
  text-align: center;
  border-radius: 50px;
  opacity: 1;
  position: fixed;
  bottom: calc(1rem + var(--notification-index, 0) * 4rem);
  right: 1rem;
  z-index: calc(
    1000 - var(--notification-index, 0)
  ); /* Höhere Benachrichtigungen weiter oben */
  animation: move 5s forwards;
  overflow: hidden;
  word-wrap: break-word;
  white-space: normal;
  display: flex;
  align-items: center;
  gap: 1rem;
  animation-duration: var(--notification-duration, 5s);
}
.notification.clickable {
  cursor: pointer;
}

.notification p {
  margin: 0;
  word-break: break-word;
  overflow-wrap: break-word;
}

.notification__action {
  border: none;
  background: transparent;
  color: #93c5fd;
  font: inherit;
  font-size: 0.95rem;
  font-weight: 900;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
}

.notification__action:hover,
.notification__action:focus-visible {
  color: #bfdbfe;
  text-decoration: underline;
  outline: none;
}

@keyframes move {
  0% {
    transform: translateY(100vh);
  }
  20%,
  80% {
    transform: translateY(-1rem);
  }
  100% {
    transform: translateY(100vh);
  }
}

@media (max-width: 768px) {
  .notification {
    font-size: 0.9rem;
    padding: 0.8rem 1.5rem;
    max-width: 95vw;
    gap: 0.75rem;
  }
}
</style>
